// src/services/api.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // This ensures cookies are sent with requests
});

// Generic API request wrapper to eliminate repetitive try-catch blocks
const apiRequest = async (requestFn) => {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Network error' };
  }
};

export const api = {
  login: (credentials) => apiRequest(() => axiosInstance.post('/login', credentials)),
  getCertificazioni: () => apiRequest(() => axiosInstance.get('/certificazioni')),
  getOrders: (role, userId, userType) => {
    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (userId) params.append('user_id', userId);
    if (userType) params.append('user_type', userType);
    const query = params.toString();
    return apiRequest(() => axiosInstance.get(`/orders${query ? '?' + query : ''}`));
  },
  getOrderById: (id) => apiRequest(() => axiosInstance.get(`/orders/${id}`)),
  createOrder: (orderData) => apiRequest(() => axiosInstance.post('/orders', orderData)),

  updateOrder: (id, orderData) => apiRequest(() => axiosInstance.put(`/orders/${id}`, orderData)),

  // Admin-specific API endpoints
  getUsers: () => apiRequest(() => axiosInstance.get('/users')),
  createUser: (userData) => apiRequest(() => axiosInstance.post('/users', userData)),
  updateUser: (id, userData) => apiRequest(() => axiosInstance.put(`/users/${id}`, userData)),
  deleteUser: (id) => apiRequest(() => axiosInstance.delete(`/users/${id}`)),

  getClients: () => apiRequest(() => axiosInstance.get('/clients')),
  getClientsDropdown: () => apiRequest(() => axiosInstance.get('/clients-dropdown')),
  getClientById: (id) => apiRequest(() => axiosInstance.get(`/clients/${id}`)),
  createClient: (clientData) => apiRequest(() => axiosInstance.post('/clients', clientData)),
  updateClient: (id, clientData) => apiRequest(() => axiosInstance.put(`/clients/${id}`, clientData)),
  deleteClient: (id) => apiRequest(() => axiosInstance.delete(`/clients/${id}`)),
  createProspect: (prospectData) => apiRequest(() => axiosInstance.post('/prospects', prospectData)),

  getProducts: () => apiRequest(() => axiosInstance.get('/products')),
  createProduct: (productData) => apiRequest(() => axiosInstance.post('/products', productData)),
  updateProduct: (id, productData) => apiRequest(() => axiosInstance.put(`/products/${id}`, productData)),
  deleteProduct: (id) => apiRequest(() => axiosInstance.delete(`/products/${id}`)),
  deleteOrder: (id) => apiRequest(() => axiosInstance.delete(`/orders/${id}`)),
  resendOrderEmail: (orderId) => apiRequest(() => axiosInstance.post(`/orders/${orderId}/resend-email`)),
  duplicateOrder: (orderId, createdById, createdByType, requester = {}) => apiRequest(() => axiosInstance.post(`/orders/${orderId}/duplicate`, {
    created_by_id: createdById,
    created_by_type: createdByType,
    role: requester.role,
    user_id: requester.userId,
    user_type: requester.userType
  })),

  // Statistics endpoints
  getGeneralStats: async () => {
    try {
      const response = await axiosInstance.get('/stats/general');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getCustomStats: async (params) => {
    try {
      const response = await axiosInstance.get('/stats/custom', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Configuration endpoints
  getSystemConfig: async () => {
    try {
      const response = await axiosInstance.get('/config');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  updateSystemConfig: async (configData) => {
    try {
      const response = await axiosInstance.put('/config', configData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getMaintenanceStatus: async () => {
    try {
      const response = await axiosInstance.get('/config/maintenance');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  toggleMaintenanceMode: async (data) => {
    try {
      const response = await axiosInstance.put('/config/maintenance', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getGeneralSettings: async () => {
    try {
      const response = await axiosInstance.get('/generals');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  updateGeneralSetting: async (key, value, description) => {
    try {
      const response = await axiosInstance.put('/generals', { key, value, description });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Articles endpoints
  getArticles: async () => {
    try {
      const response = await axiosInstance.get('/articles');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getArticleById: async (id) => {
    try {
      const response = await axiosInstance.get(`/articles/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  createArticle: async (articleData) => {
    try {
      const response = await axiosInstance.post('/articles', articleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  updateArticle: async (id, articleData) => {
    try {
      const response = await axiosInstance.put(`/articles/${id}`, articleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  deleteArticle: async (id) => {
    try {
      const response = await axiosInstance.delete(`/articles/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  importArticlesCSV: async (csvFile) => {
    try {
      // Create form data to send the file
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      // Use multipart/form-data for file upload
      const response = await axios.post(`${API_URL}/articles/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Ricambi endpoints
  getRicambi: async (search = '') => {
    try {
      const params = search ? { search } : {};
      const response = await axiosInstance.get('/ricambi', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Agents endpoints
  getAgents: async () => {
    try {
      const response = await axiosInstance.get('/agents');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getAgentById: async (id) => {
    try {
      const response = await axiosInstance.get(`/agents/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getAgentClients: async (agentId) => {
    try {
      const response = await axiosInstance.get(`/agents/${agentId}/clients`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  createAgent: async (agentData) => {
    try {
      const response = await axiosInstance.post('/agents', agentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  updateAgent: async (id, agentData) => {
    try {
      const response = await axiosInstance.put(`/agents/${id}`, agentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  deleteAgent: async (id) => {
    try {
      const response = await axiosInstance.delete(`/agents/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Draft orders endpoints
  getDraftOrders: async (userId, userType = 'client', customerId = null) => {
    try {
      let url = `/draft-orders?user_id=${userId}&user_type=${userType}`;
      if (customerId) {
        url += `&customer_id=${customerId}`;
      }
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getDraftOrderById: async (id) => {
    try {
      const response = await axiosInstance.get(`/draft-orders/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  createDraftOrder: async (draftOrderData) => {
    try {
      const response = await axiosInstance.post('/draft-orders', draftOrderData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  updateDraftOrder: async (id, draftOrderData) => {
    try {
      const response = await axiosInstance.put(`/draft-orders/${id}`, draftOrderData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  deleteDraftOrder: async (id) => {
    try {
      const response = await axiosInstance.delete(`/draft-orders/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  addCartItem: async (draftOrderId, productData) => {
    try {
      const response = await axiosInstance.post(`/draft-orders/${draftOrderId}/cart-items`, {
        product_data: productData
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  updateCartItem: async (draftOrderId, cartItemId, productData) => {
    try {
      const response = await axiosInstance.put(`/draft-orders/${draftOrderId}/cart-items/${cartItemId}`, {
        product_data: productData
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  deleteCartItem: async (draftOrderId, cartItemId) => {
    try {
      const response = await axiosInstance.delete(`/draft-orders/${draftOrderId}/cart-items/${cartItemId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  completeDraftOrder: async (draftOrderId, orderInfo = {}) => {
    try {
      // First get the draft order details
      const draftOrderResponse = await axiosInstance.get(`/draft-orders/${draftOrderId}`);
      const draftOrder = draftOrderResponse.data.draft_order;

      // Prepare delivery info and notes
      const deliveryInfo = {};
      const notes = [];

      // Extract delivery information
      if (orderInfo.deliveryAddress) {
        deliveryInfo.delivery_address = orderInfo.deliveryAddress;
      }
      if (orderInfo.deliveryCity) {
        deliveryInfo.delivery_city = orderInfo.deliveryCity;
      }
      if (orderInfo.deliveryZip) {
        deliveryInfo.delivery_zip = orderInfo.deliveryZip;
      }
      if (orderInfo.deliveryProvince) {
        deliveryInfo.delivery_province = orderInfo.deliveryProvince;
      }
      if (orderInfo.deliveryDate) {
        deliveryInfo.requested_delivery_date = orderInfo.deliveryDate;
      }

      // Combine all notes
      if (orderInfo.deliveryDate) {
        notes.push(`Data consegna richiesta: ${orderInfo.deliveryDate}`);
      }
      if (orderInfo.notes) {
        notes.push(orderInfo.notes);
      }

      // Then complete it with the full data
      const response = await axiosInstance.post('/draft-orders/complete', {
        draft_order_id: draftOrderId,
        draft_order: draftOrder,
        delivery_info: Object.keys(deliveryInfo).length > 0 ? deliveryInfo : null,
        order_notes: notes.length > 0 ? notes.join('\n\n') : null,
        subtotal: orderInfo.subtotal,
        discountPercentage: orderInfo.discountPercentage,
        discountAmount: orderInfo.discountAmount,
        extraDiscountPercentage: orderInfo.extraDiscountPercentage,
        extraDiscountAmount: orderInfo.extraDiscountAmount,
        total: orderInfo.total,
        orderType: orderInfo.orderType,
        sentByName: orderInfo.sentByName,
        sentByRole: orderInfo.sentByRole,
        ...(orderInfo.variants && orderInfo.variants.length > 0 ? { variants: orderInfo.variants } : {}),
        ...(orderInfo.replacesOrderNumber ? { replacesOrderNumber: orderInfo.replacesOrderNumber, requester: orderInfo.requester } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Error completing draft order:', error.response?.data || error.message);
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Add the submitOrder method that was referenced in FinalOrderReview.js but not defined
  submitOrder: async (orderDetails) => {
    try {
      const response = await axiosInstance.post('/orders', orderDetails);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Upload project files for progettazione
  uploadProjectFiles: async (files) => {
    try {
      const formData = new FormData();
      
      // Add files to FormData
      for (let i = 0; i < files.length; i++) {
        formData.append('files[]', files[i]);
      }

      const response = await axios.post(`${API_URL}/upload-project-files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Save uploaded project files to database
  saveProjectFiles: async (cartItemId, filesData, orderId = null, projectDescription = null) => {
    try {
      const response = await axiosInstance.post('/save-project-files', {
        cart_item_id: cartItemId,
        files: filesData,
        order_id: orderId,
        project_description: projectDescription
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get order items for a specific order
  getOrderItems: async (orderId) => {
    try {
      const response = await axiosInstance.get(`/orders/${orderId}/items`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get orders and quotes status for logged-in user
  getOrdersStatus: async (customerCode, salesperson, dateFrom, dateTo, filterAgent, isAdmin = false) => {
    try {
      const params = new URLSearchParams();
      if (customerCode) {
        params.append('customer_code', customerCode);
      }
      if (salesperson) {
        params.append('salesperson', salesperson);
      }
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }
      if (filterAgent) {
        params.append('filter_agent', filterAgent);
      }
      if (isAdmin) {
        params.append('is_admin', '1');
      }
      const response = await axiosInstance.get(`/orders-status?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get order details with line items
  getOrderDetails: async (orderId) => {
    try {
      const response = await axiosInstance.get(`/order-details/${orderId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get quote details with line items
  getQuoteDetails: async (quoteId) => {
    try {
      const response = await axiosInstance.get(`/quote-details/${quoteId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // ==================== QUOTES/OFFERS API ====================

  // Get all quotes with optional filters
  getQuotes: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.customer_code) queryParams.append('customer_code', params.customer_code);
      if (params.salesperson) queryParams.append('salesperson', params.salesperson);
      if (params.status) queryParams.append('status', params.status);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);

      const response = await axiosInstance.get(`/quotes?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get single quote by ID
  getQuoteById: async (quoteId) => {
    try {
      const response = await axiosInstance.get(`/quotes/${quoteId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Create new quote
  createQuote: async (quoteData) => {
    try {
      const response = await axiosInstance.post('/quotes', quoteData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Update quote
  updateQuote: async (quoteId, quoteData) => {
    try {
      const response = await axiosInstance.put(`/quotes/${quoteId}`, quoteData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Delete quote
  deleteQuote: async (quoteId) => {
    try {
      const response = await axiosInstance.delete(`/quotes/${quoteId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Update quote status only
  updateQuoteStatus: async (quoteId, status) => {
    try {
      const response = await axiosInstance.put(`/quotes/${quoteId}/status`, { StatoOfferta: status });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Convert quote to order
  convertQuoteToOrder: async (quoteId, additionalData = {}) => {
    try {
      const response = await axiosInstance.post(`/quotes/${quoteId}/convert-to-order`, additionalData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get available quote statuses
  getQuoteStatuses: async () => {
    try {
      const response = await axiosInstance.get('/quote-statuses');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // ==================== DOCUMENTS API ====================

  // Get general documents (visible to all)
  getGeneralDocuments: async () => {
    try {
      const response = await axiosInstance.get('/documents-general');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Upload general document
  uploadGeneralDocument: async (file, title, description = '', category = 'general') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('created_by', 'admin');

      const response = await axios.post(`${API_URL}/documents-general`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Update general document
  updateGeneralDocument: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/documents-general/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Delete general document
  deleteGeneralDocument: async (id) => {
    try {
      const response = await axiosInstance.delete(`/documents-general/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get private documents for a specific owner
  getPrivateDocuments: async (ownerType, ownerId) => {
    try {
      const response = await axiosInstance.get(`/documents-private?owner_type=${ownerType}&owner_id=${ownerId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Upload private document
  uploadPrivateDocument: async (file, ownerType, ownerId, title, description = '', category = 'private') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('owner_type', ownerType);
      formData.append('owner_id', ownerId);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('created_by', 'admin');

      const response = await axios.post(`${API_URL}/documents-private`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Update private document
  updatePrivateDocument: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/documents-private/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Delete private document
  deletePrivateDocument: async (id) => {
    try {
      const response = await axiosInstance.delete(`/documents-private/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get all documents (admin)
  getAllDocuments: async () => {
    try {
      const response = await axiosInstance.get('/documents-all');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // Get user documents (general + private for specific user)
  getUserDocuments: async (userType, userId) => {
    try {
      const response = await axiosInstance.get(`/user-documents?user_type=${userType}&user_id=${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  // ==================== CAPITOLATI API ====================

  getCapitolati: async (creatorId, isAdmin = false) => {
    try {
      const params = new URLSearchParams();
      if (creatorId) params.append('creator_id', creatorId);
      if (isAdmin) params.append('is_admin', '1');
      const response = await axiosInstance.get(`/capitolati?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getCapitolatoById: async (id) => {
    try {
      const response = await axiosInstance.get(`/capitolati/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  createCapitolato: async (formData, pdfBlob, pdfFilename, creatorInfo, revisionInfo = {}) => {
    try {
      const data = new FormData();
      data.append('form_data', JSON.stringify(formData));
      data.append('creator_id', creatorInfo.id);
      data.append('creator_name', creatorInfo.name);
      data.append('creator_role', creatorInfo.role);

      if (revisionInfo.parentId) {
        data.append('parent_capitolato_id', revisionInfo.parentId);
        data.append('revision', revisionInfo.revision);
      }

      if (pdfBlob) {
        data.append('pdf_file', pdfBlob, pdfFilename);
      }

      const response = await axios.post(`${API_URL}/capitolati`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  },

  getCapitolatoPdfUrl: (id) => `${API_URL}/capitolati/${id}/pdf`,

  deleteCapitolato: async (id) => {
    try {
      const response = await axiosInstance.delete(`/capitolati/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Network error' };
    }
  }
};
